import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { AdminApi } from '../api';
import { ActivityLogDetail, CreateActivityLogRequest } from '../models/activity-log.model';

@Injectable({ providedIn: 'root' })
export class ActivityLogService {
  private readonly adminApi = inject(AdminApi.Client);

  async logActivity(request: CreateActivityLogRequest): Promise<void> {
    return this.logActivities([request]);
  }

  async logActivityAsync(request: CreateActivityLogRequest): Promise<void> {
    return this.logActivities([request]);
  }

  async logActivities(requests: CreateActivityLogRequest[]): Promise<void> {
    if (!requests.length) {
      return;
    }

    for (let index = 0; index < requests.length; index += 50) {
      const batch = requests.slice(index, index + 50).map((request) => this.toAdminApiRequest(request));

      try {
        await firstValueFrom(this.adminApi.batch(batch));
      } catch (error) {
        console.error('Failed to submit activity logs batch', error);
      }
    }
  }

  batchActivityLogs(requests: CreateActivityLogRequest[]): Observable<boolean> {
    const batch = requests.map((request) => this.toAdminApiRequest(request));
    return this.adminApi.batch(batch);
  }

  private toAdminApiRequest(request: CreateActivityLogRequest): AdminApi.CreateActivityLogRequest {
    return new AdminApi.CreateActivityLogRequest({
      eventTypeCode: request.eventTypeCode,
      moduleName: request.moduleName,
      entityTypeName: request.entityTypeName,
      actionTemplate: request.actionTemplate,
      entityId: request.entityId,
      oldValues: request.oldValues,
      newValues: request.newValues,
      createdDate: request.createdDate,
      activityDetails: request.activityDetails?.map((detail) => this.toAdminApiActivityDetail(detail)),
    });
  }

  private toAdminApiActivityDetail(detail: ActivityLogDetail): AdminApi.ActivityLogDetailItem {
    return new AdminApi.ActivityLogDetailItem({
      stepName: detail.stepName,
      stepDescription: detail.stepDescription,
      status: detail.status,
      sequenceNo: detail.sequenceNo,
      startTime: detail.startTime,
      endTime: detail.endTime,
      durationMs: detail.durationMs,
      requestPayload: detail.requestPayload ?? undefined,
      responsePayload: detail.responsePayload ?? undefined,
      errorMessage: detail.errorMessage ?? undefined,
    });
  }
}
